(function () {
  // ===== CONFIG =====
  // For dev:
  // const TRACKING_ENDPOINT = "http://localhost:8000/collect";
  // For prod:
  const TRACKING_ENDPOINT = "https://api.azga.in/collect";
  const TWO_YEARS_SECONDS = 60 * 60 * 24 * 365 * 2;
  const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes of inactivity rolls session

  function getCookie(name) {
    const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[2]) : null;
  }

  function getCookieOptions() {
    const isHttps = window.location.protocol === "https:";
    const isAzgaDomain = /\.azga\.in$/.test(window.location.hostname);
    return {
      domain: isAzgaDomain ? ".azga.in" : undefined,
      secure: isHttps,
    };
  }

  function setCookie(name, value, maxAgeSeconds) {
    const opts = getCookieOptions();
    const parts = [name + "=" + encodeURIComponent(value), "path=/", "samesite=lax"];
    if (maxAgeSeconds) {
      parts.push("max-age=" + String(maxAgeSeconds));
    }
    if (opts.domain) {
      parts.push("domain=" + opts.domain);
    }
    if (opts.secure) {
      parts.push("secure");
    }
    document.cookie = parts.join("; ");
  }

  function getPath() {
    try {
      return window.location.pathname + window.location.search;
    } catch (e) {
      return null;
    }
  }

  function getLandingPage() {
    const key = "azga_landing_page";
    let lp = localStorage.getItem(key);
    if (!lp) {
      lp = window.location.href;
      localStorage.setItem(key, lp);
    }
    return lp;
  }

  function getDeviceType() {
    const ua = navigator.userAgent || "";
    if (/mobi/i.test(ua)) return "mobile";
    if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
    return "desktop";
  }

  function getQueryParam(name) {
    const url = new URL(window.location.href);
    return url.searchParams.get(name);
  }

  function getOrCreateAzgaUid() {
    let uid = getCookie("azga_uid");
    if (!uid) {
      uid = "azga_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      // 2 years
      setCookie("azga_uid", uid, TWO_YEARS_SECONDS);
    }
    return uid;
  }

  function getGaClientIdFromCookie() {
    const raw = getCookie("_ga");
    if (!raw) return null;
    const match = raw.match(/^GA\d+\.\d+\.(.+)$/);
    return match ? match[1] : raw;
  }

  function getOrCreateCid() {
    let cid = getCookie("cid");
    if (!cid) {
      cid = getGaClientIdFromCookie();
      if (cid) {
        // 2 years
        setCookie("cid", cid, TWO_YEARS_SECONDS);
      }
    }
    return cid;
  }

  function getSessionId() {
    const key = "azga_session_id";
    const lastSeenKey = "azga_session_last_seen";
    const now = Date.now();
    let sid = localStorage.getItem(key);
    const lastSeen = parseInt(localStorage.getItem(lastSeenKey) || "0", 10);
    const expired = !lastSeen || now - lastSeen > SESSION_TTL_MS;
    if (!sid || expired) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(key, sid);
    }
    localStorage.setItem(lastSeenKey, String(now));
    return sid;
  }

  function persistInitialUtms() {
    const map = {
      utm_source: getQueryParam("utm_source"),
      utm_medium: getQueryParam("utm_medium"),
      utm_campaign: getQueryParam("utm_campaign"),
      utm_content: getQueryParam("utm_content"),
      utm_term: getQueryParam("utm_term"),
      referrer: document.referrer || null,
    };
    Object.keys(map).forEach(function (k) {
      const storeKey = "azga_first_" + k;
      if (!localStorage.getItem(storeKey) && map[k]) {
        localStorage.setItem(storeKey, map[k]);
      }
    });
  }

  // Keep some state we reuse
  const state = {
    cartToken: null,
    checkoutToken: null,
  };

  function captureCartToken() {
    // Fetch cart once to grab token; Shopify returns cart JSON with "token"
    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (cart && cart.token) {
          state.cartToken = cart.token;
        }
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  function updateCartAttributes(sessionId, azgaUid, fbp, cid) {
    // Persist session/user ids onto cart for webhook stitching
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        attributes: {
          azga_session_id: sessionId,
          azga_uid: azgaUid,
          fbp: fbp || null,
          cid: cid || null,
        },
      }),
    }).catch(function () {});
  }

  function getShopifyMeta() {
    const meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || null;
    if (!meta) return null;
    const product = meta.product
      ? {
          id: meta.product.id || null,
          gid: meta.product.gid || null,
          handle: meta.product.handle || null,
          type: meta.product.type || null,
          vendor: meta.product.vendor || null,
          selectedVariantId: meta.selectedVariantId || null,
          variants: Array.isArray(meta.product.variants)
            ? meta.product.variants.map(function (v) {
                return {
                  id: v.id || null,
                  name: v.name || null,
                  price: v.price || null,
                  sku: v.sku || null,
                };
              })
            : null,
        }
      : null;
    return {
      currency: meta.currency || null,
      page: meta.page || null,
      product: product,
      selectedVariantId: meta.selectedVariantId || null,
    };
  }

  function basePayload() {
    const azga_uid = getOrCreateAzgaUid();
    const cid = getOrCreateCid();
    const fbp = getCookie("_fbp");
    const session_id = getSessionId();
    persistInitialUtms();
    // ensure cart attributes get written once per page load
    updateCartAttributes(session_id, azga_uid, fbp, cid);
    return {
      session_id: session_id,
      azga_uid: azga_uid,
      cid: cid,
      url: window.location.href,
      path: getPath(),
      landing_page: getLandingPage(),
      referrer: document.referrer || null,
      utm_source: getQueryParam("utm_source"),
      utm_medium: getQueryParam("utm_medium"),
      utm_campaign: getQueryParam("utm_campaign"),
      utm_content: getQueryParam("utm_content"),
      utm_term: getQueryParam("utm_term"),
      first_utm_source: localStorage.getItem("azga_first_utm_source"),
      first_utm_medium: localStorage.getItem("azga_first_utm_medium"),
      first_utm_campaign: localStorage.getItem("azga_first_utm_campaign"),
      first_utm_content: localStorage.getItem("azga_first_utm_content"),
      first_utm_term: localStorage.getItem("azga_first_utm_term"),
      first_referrer: localStorage.getItem("azga_first_referrer"),
      fbclid: getQueryParam("fbclid"),
      gclid: getQueryParam("gclid"),
      fbp: getCookie("_fbp"),
      fbc: getCookie("_fbc"), // if you set it
      ttclid: getQueryParam("ttclid"),
      ga_client_id: getCookie("_ga"),
      ga_session_id: null, // can parse _ga_ cookie later if needed
      user_agent: navigator.userAgent || null,
      language: navigator.language || null,
      screen: (typeof screen !== "undefined" ? screen.width + "x" + screen.height : null),
      device_type: getDeviceType(),
      cart_token: state.cartToken,
      checkout_token: state.checkoutToken,
    };
  }

  function sendEvent(event_name, payload) {
    const body = Object.assign({}, basePayload(), {
      event_name: event_name,
      payload: payload || {},
    });

    // Fire and forget
    fetch(TRACKING_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true, // better when navigating away
    }).catch(function (err) {
      if (window.console) console.warn("tracking error", err);
    });
  }

  function trackPageView() {
    const shopifyMeta = getShopifyMeta();
    sendEvent("page_view", {
      page_title: document.title || null,
      shopify: shopifyMeta,
    });
    if (window.meta && window.meta.product) {
      sendEvent("product_view", {
        product_id: window.meta.product.id,
        title: window.meta.product.title,
        price: window.meta.product.price,
        currency: window.meta.product.currency || null,
        handle: window.meta.product.handle || null,
        shopify: shopifyMeta,
      });
    }
  }

  function hookAddToCart() {
    // Clicks on add-to-cart buttons
    document.body.addEventListener("click", function (e) {
      const btn = e.target.closest("[data-add-to-cart], button[name='add'], button[type='submit'][name='add']");
      if (!btn) return;
      sendEvent("add_to_cart_click", {});
    });

    // Intercept /cart/add fetch or XHR
    const origFetch = window.fetch;
    window.fetch = function () {
      const args = arguments;
      const url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url);
      const resPromise = origFetch.apply(this, args);
      try {
        if (url && url.indexOf("/cart/add") !== -1) {
          resPromise
            .then(function (res) {
              return res.clone().json();
            })
            .then(function (data) {
              // capture cart token and cart snapshot after add for full context
              captureCartToken()
                .then(function (cart) {
                  sendEvent("add_to_cart", {
                    cart_add_response: data,
                    cart_snapshot: cart || null,
                  });
                })
                .catch(function () {
                  sendEvent("add_to_cart", { cart_add_response: data });
                });
            })
            .catch(function () {});
        }
      } catch (e) {}
      return resPromise;
    };
  }

  function hookBeginCheckout() {
    let lastCheckoutClickAt = 0;
    let lastBeginCheckoutAt = 0;
    const CLICK_DEDUP_MS = 500;
    const HOST_EVENT_TYPES = ["pointerdown", "touchstart", "mousedown"];
    const CHECKOUT_BTN_EVENT_TYPES = ["pointerdown", "touchstart", "mousedown", "click"];
    function getActionPath(action) {
      const raw = action || "";
      let path = raw;
      try {
        path = new URL(raw, window.location.origin).pathname;
      } catch (e) {
        path = raw.split("?")[0];
      }
      return path;
    }

    function isCheckoutAction(action) {
      const path = getActionPath(action);
      if (path === "/cart") return true;
      return path === "/checkout" || path.startsWith("/checkout/");
    }

    function isCheckoutFormSubmit(btn) {
      if (!btn) return false;
      const form = btn.closest("form");
      if (!form) return false;
      const action = form.getAttribute("action") || "";
      if (!isCheckoutAction(action)) return false;
      const type = (btn.getAttribute("type") || "").toLowerCase();
      const name = (btn.getAttribute("name") || "").toLowerCase();
      return type === "submit" || name === "checkout";
    }

    function findCheckoutTarget(event) {
      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      for (let i = 0; i < path.length; i += 1) {
        const el = path[i];
        if (!(el instanceof Element)) continue;
        if (el.matches("a[href*='/checkout'], button[data-testid='Checkout-button'], button[name='checkout']")) {
          return el;
        }
        if (el.matches("[data-shopify='payment-button'], shopify-accelerated-checkout")) {
          return el;
        }
        if (el.tagName === "SHOPIFY-BUY-IT-NOW-BUTTON") {
          return el;
        }
      }
      return event.target.closest(
        "a[href*='/checkout'], button[data-testid='Checkout-button'], button[name='checkout'], shopify-buy-it-now-button button, [data-shopify='payment-button']"
      );
    }

    function trackBeginCheckout() {
      // best effort: ensure we have cart token
      captureCartToken();
      sendEvent("begin_checkout", {
        cart_token: state.cartToken,
      });
    }

    function fireBeginCheckout() {
      const now = Date.now();
      if (now - lastBeginCheckoutAt < CLICK_DEDUP_MS) return;
      lastBeginCheckoutAt = now;
      lastCheckoutClickAt = now;
      trackBeginCheckout();
    }

    function bindCheckoutHost(host) {
      if (!host || host.__azgaCheckoutHostBound) return;
      host.__azgaCheckoutHostBound = true;
      for (let i = 0; i < HOST_EVENT_TYPES.length; i += 1) {
        host.addEventListener(HOST_EVENT_TYPES[i], function () {
          fireBeginCheckout();
        }, true);
      }
    }

    function bindCheckoutHosts() {
      const hosts = document.querySelectorAll("shopify-accelerated-checkout");
      for (let i = 0; i < hosts.length; i += 1) {
        bindCheckoutHost(hosts[i]);
      }
    }

    function bindCheckoutButton(btn) {
      if (!btn || btn.__azgaCheckoutBtnBound) return;
      btn.__azgaCheckoutBtnBound = true;
      for (let i = 0; i < CHECKOUT_BTN_EVENT_TYPES.length; i += 1) {
        btn.addEventListener(CHECKOUT_BTN_EVENT_TYPES[i], function () {
          fireBeginCheckout();
        }, true);
      }
    }

    function bindCheckoutButtons() {
      const buttons = document.querySelectorAll(
        "cart-drawer button[name='checkout'], cart-drawer input[name='checkout'], form[action^='/cart'] button[name='checkout'], form[action^='/cart'] input[name='checkout']"
      );
      for (let i = 0; i < buttons.length; i += 1) {
        bindCheckoutButton(buttons[i]);
      }
    }

    bindCheckoutHosts();
    bindCheckoutButtons();
    try {
      const mo = new MutationObserver(function (muts) {
        for (let i = 0; i < muts.length; i += 1) {
          const nodes = muts[i].addedNodes || [];
          for (let j = 0; j < nodes.length; j += 1) {
            const node = nodes[j];
            if (!(node instanceof Element)) continue;
            if (node.tagName === "SHOPIFY-ACCELERATED-CHECKOUT") {
              bindCheckoutHost(node);
            } else if (node.querySelectorAll) {
              const nested = node.querySelectorAll("shopify-accelerated-checkout");
              for (let k = 0; k < nested.length; k += 1) {
                bindCheckoutHost(nested[k]);
              }
            }
            if (node.matches && node.matches("button[name='checkout'], input[name='checkout']")) {
              bindCheckoutButton(node);
            } else if (node.querySelectorAll) {
              const checkoutButtons = node.querySelectorAll(
                "button[name='checkout'], input[name='checkout']"
              );
              for (let k = 0; k < checkoutButtons.length; k += 1) {
                bindCheckoutButton(checkoutButtons[k]);
              }
            }
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}

    document.body.addEventListener("click", function (e) {
      const btn = findCheckoutTarget(e);
      if (!btn) return;
      fireBeginCheckout();
    });

    // Capture checkout when themes submit forms directly
    document.body.addEventListener("submit", function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (Date.now() - lastCheckoutClickAt < CLICK_DEDUP_MS) return;
      const action = form.getAttribute("action") || "";
      const actionPath = getActionPath(action);
      if (
        actionPath.startsWith("/cart/add") ||
        actionPath.startsWith("/cart/update") ||
        actionPath.startsWith("/cart/change")
      ) {
        return;
      }
      const submitter = e.submitter instanceof Element ? e.submitter : null;
      if (!isCheckoutAction(action)) return;
      if (actionPath === "/cart") {
        if (!submitter || !isCheckoutFormSubmit(submitter)) return;
      } else if (submitter && !isCheckoutFormSubmit(submitter)) {
        return;
      }
      if (isCheckoutAction(action)) {
        trackBeginCheckout();
      }
    });
  }

  function detectCheckoutPage() {
    try {
      if (window.Shopify && window.Shopify.checkout) {
        state.checkoutToken = window.Shopify.checkout.token || null;
        sendEvent("begin_checkout", {
          checkout_token: state.checkoutToken,
          cart_token: state.cartToken,
          currency: window.Shopify.checkout.currency || null,
          total_price: window.Shopify.checkout.total_price || null,
        });
      }
    } catch (e) {}
  }

  document.addEventListener("DOMContentLoaded", function () {
    try {
      captureCartToken();
      detectCheckoutPage();
      trackPageView();
      hookAddToCart();
      hookBeginCheckout();
    } catch (e) {
      if (window.console) console.warn("tracking init error", e);
    }
  });
})();
